import { CommonModule, formatCurrency, formatDate, formatPercent } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, linkedSignal, signal } from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { ReactiveFormsModule } from "@angular/forms";
import { AdSpendFeeType, Currency, Customer, Plan } from "@front/m19-api-client";
import { blacklistDspPlan, CurrencySymbols } from "@front/m19-models";
import { AuthService, BillingPlanService, BillingService, UpgradePlanService } from "@front/m19-services";
import {
  BillingInfoFormComponent,
  BillingInfoFormValue,
  IIconComponent,
  ISwitchButtonComponent,
  ModalRef,
  SpinnerComponent,
  ToasterService,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { DateTime } from "luxon";
import { StripeService } from "ngx-stripe";
import { environment } from "../../environments/environment";
import { switchMap } from "rxjs";

@Component({
  selector: "app-upgrade-to-pro-modal",
  imports: [
    CommonModule,
    TranslocoDirective,
    ReactiveFormsModule,
    SpinnerComponent,
    BillingInfoFormComponent,
    ISwitchButtonComponent,
    IIconComponent,
  ],
  templateUrl: "./upgrade-to-pro-modal.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UpgradeToProModalComponent {
  private readonly billingService = inject(BillingService);
  private readonly billingPlanService = inject(BillingPlanService);
  private readonly toasterService = inject(ToasterService);
  private readonly authService = inject(AuthService);
  private readonly translocoService = inject(TranslocoService);
  public stripeService = inject(StripeService);
  private readonly upgradePlanService = inject(UpgradePlanService);

  protected readonly stripePublicKey = environment.stripePublicKey;

  private readonly modalRef = inject(ModalRef);

  readonly PLAN_FEATURES: string[] = [];

  readonly PLAN = Plan.PROFESSIONAL;
  readonly supportedCurrencies = [Currency.EUR, Currency.USD];
  readonly AdSpendFeeType = AdSpendFeeType;

  private readonly organizationGroup = toSignal(this.billingService.organizationOwner$);
  private readonly organization = computed(() => this.organizationGroup()?.organization);
  readonly customer = computed(() => this.organizationGroup()?.customer);
  readonly customerCountry = linkedSignal<string | undefined>(() => this.customer()?.country);
  private readonly customerCountry$ = toObservable(this.customerCountry);

  private readonly proBillingPlan = toSignal(
    this.customerCountry$.pipe(switchMap((c) => this.billingPlanService.listProBillingPlans(c))),
  );

  private proPlanEur = computed(() => this.proBillingPlan()?.find((p) => p.currency === Currency.EUR));
  private proPlanUsd = computed(() => this.proBillingPlan()?.find((p) => p.currency === Currency.USD));

  fetchCustomerLoading = signal(false);

  user = toSignal(this.authService.loggedUser$);

  selectedCurrency = signal(Currency.EUR);
  selectedPlan = computed(() => (this.selectedCurrency() === Currency.EUR ? this.proPlanEur() : this.proPlanUsd()));

  planAmount = computed(() => (this.selectedPlan() ? this.selectedPlan()?.flatFee?.amount : undefined));
  formattedPlanAmount = computed(() => (this.planAmount() ? this.formatAmount(this.planAmount()!) : ""));
  formattedPlanVat = computed(() => this.formatAmount(this.planAmount()! * this.taxRate()));
  formattedPlanTotal = computed(() => this.formatAmount(this.planAmount()! * (1 + this.taxRate())));
  isAmountLoading = computed(() => !this.planAmount() || !isFinite(this.planAmount()!) || isNaN(this.planAmount()!));
  taxRate = signal(0);
  locale = computed(() => this.user()!.locale);
  readonly formattedDspPricing = computed(() => {
    const billingPlan = this.selectedPlan();
    if (
      !billingPlan ||
      blacklistDspPlan.has(billingPlan.plan!) ||
      !billingPlan.dspAdSpendFee ||
      !billingPlan.dspAdSpendFee.amount ||
      !billingPlan?.dspM19SeatAdSpendFee ||
      !billingPlan.dspM19SeatAdSpendFee.amount
    ) {
      return "";
    }
    return (
      formatPercent(billingPlan.dspAdSpendFee.amount, this.locale() ?? "en", "1.0-2") +
      " " +
      this.translocoService.translate("subscription-card.the_managed_dsp_ad_spend") +
      " " +
      this.translocoService.translate("common.or") +
      " " +
      formatPercent(billingPlan.dspM19SeatAdSpendFee.amount, this.locale() ?? "en", "1.0-2") +
      " " +
      this.translocoService.translate("subscription-card.total_dsp_adspend_on_m19_seat")
    );
  });

  requiredCard = computed(() => !this.organization()?.noCbRequired);
  // Mirrors the back end: an upgrade during the trial starts the plan the day after it, any other one today, and
  // the first invoice is issued at the end of that first month
  firstInvoiceDate = computed<string>(() => {
    const testEndDate = this.organization()!.testEndDate;
    const planStart =
      testEndDate && DateTime.fromISO(testEndDate) >= DateTime.utc()
        ? DateTime.fromISO(testEndDate).plus({ days: 1 })
        : DateTime.utc();
    return formatDate(planStart.plus({ months: 1 }).toISODate()!, "longDate", this.locale() ?? "en-US");
  });

  constructor() {
    this.PLAN_FEATURES = [
      this.translocoService.translate("billing-plan-selection.unlimited_account"),
      this.translocoService.translate("billing-plan-selection.unlimited_marketplace"),
      this.translocoService.translate("billing-plan-selection.unlimited_user"),
      this.translocoService.translate("billing-plan-selection.unlimited_strategy"),
      this.translocoService.translate("billing-plan-selection.access_to_dsp_strategies") + "*",
      `${this.translocoService.translate("subscription-card.up_to")} 1000 ${this.translocoService.translate("subscription-card.tracked_keywords_daily")}`,
      `${this.translocoService.translate("subscription-card.up_to")} 100 ${this.translocoService.translate("subscription-card.tracked_keywords_hourly")}`,
    ];
  }

  onSubmit(formValue: BillingInfoFormValue) {
    this.fetchCustomerLoading.set(true);

    const cardElement = formValue.cardElement;
    const paymentId = formValue.paymentId;

    // Reset the form values to avoid parse input error from backend
    formValue.cardElement = undefined;
    formValue.paymentId = undefined;

    const formCustomer: Customer = {
      ...(this.customer() || {}),
      ...formValue,
      m19UserId: this.user()!.userId,
    };

    this.upgradePlanService
      .upgradeToPlan(this.organizationGroup()!, this.selectedPlan()!, formCustomer, cardElement, paymentId)
      .subscribe({
        next: () => {
          this.toasterService.success("Successfully upgraded to Pro");
          this.fetchCustomerLoading.set(false);
          this.modalRef.close();
        },
        error: (error) => {
          this.toasterService.error(error);
          this.fetchCustomerLoading.set(false);
        },
      });
  }

  private formatAmount(amount: number) {
    return formatCurrency(
      amount,
      this.locale(),
      CurrencySymbols[this.selectedCurrency()],
      this.selectedCurrency(),
      "1.0-0",
    );
  }
}
